import { useEffect, useState } from 'react'
import { Lock, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import { IPReputationTab } from '@/components/security/IPReputationTab'
import { SecurityEventsTab } from '@/components/security/SecurityEventsTab'
import { RateLimitViolationsTab } from '@/components/security/RateLimitViolationsTab'
import { BansTab } from '@/components/security/BansTab'
import { LegalHoldsTab } from '@/components/security/LegalHoldsTab'
import { TwoFactorTab } from '@/components/security/TwoFactorTab'

interface SecurityStats {
  total_events_24h: number
  critical_events_24h: number
  active_bans: number
  pending_reviews: number
  rate_limit_violations_24h: number
  brute_force_attempts_24h: number
}

// CHECKPOINT: PRD-09 FR-9.13 — Admin security hub with events, violations, bans, holds, and 2FA
export function Security() {
  const [stats, setStats] = useState<SecurityStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/admin/security/dashboard')
        setStats(res.data)
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to load security stats'))
      } finally {
        setStatsLoading(false)
      }
    }
    fetchStats()
  }, [])

  const statCards = stats
    ? [
        { label: 'Events 24h', value: stats.total_events_24h },
        { label: 'Critical', value: stats.critical_events_24h, color: 'text-red-500' },
        { label: 'Active Bans', value: stats.active_bans },
        { label: 'Pending Reviews', value: stats.pending_reviews, color: 'text-amber-500' },
        { label: 'Rate Limit Hits 24h', value: stats.rate_limit_violations_24h },
        { label: 'Brute Force 24h', value: stats.brute_force_attempts_24h, color: 'text-red-500' },
      ]
    : []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Lock className="h-5 w-5 text-primary" />
          Security
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Abuse prevention, security events, and access control</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statsLoading ? (
          <Card className="col-span-full">
            <CardContent className="p-4 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : (
          statCards.map((s) => (
            <Card key={s.label}>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color || ''}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Tabs defaultValue="events" className="space-y-4">
        <TabsList>
          <TabsTrigger value="events">Security Events</TabsTrigger>
          <TabsTrigger value="rate-limits">Rate Limits</TabsTrigger>
          <TabsTrigger value="bans">Bans</TabsTrigger>
          <TabsTrigger value="legal-holds">Legal Holds</TabsTrigger>
          <TabsTrigger value="reputation">IP Reputation</TabsTrigger>
          <TabsTrigger value="2fa">2FA Setup</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          <SecurityEventsTab />
        </TabsContent>
        <TabsContent value="rate-limits">
          <RateLimitViolationsTab />
        </TabsContent>
        <TabsContent value="bans">
          <BansTab />
        </TabsContent>
        <TabsContent value="legal-holds">
          <LegalHoldsTab />
        </TabsContent>
        <TabsContent value="reputation">
          <IPReputationTab />
        </TabsContent>
        <TabsContent value="2fa">
          <TwoFactorTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
